import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { DataTable, DatePicker, EmptyState, ErrorNotice, LoadingState, Panel, SearchInput, SegmentedControl } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { periodLabel } from '@/lib/report-window';
import { readPageMovers, type MoverMetric, type MoverRow } from '@/lib/pages';
import { DeltaPct } from '@/components/traffic-sources';

/**
 * "Which pages dropped?" — the investigation panel for a bad day or week.
 * Pick a day (or the week / four weeks ending on it) and the panel ranks
 * pages by change vs the SAME weekday(s) the week — or four weeks — before,
 * so a Monday is never judged against a Sunday.
 */

const METRIC_OPTIONS: readonly { key: MoverMetric; label: string }[] = [
  { key: 'clicks', label: 'Clicks (organic)' },
  { key: 'sessions', label: 'Sessions (all)' },
  { key: 'impressions', label: 'Impressions' },
];

/** Comparison spans — multiples of 7 days so weekdays stay aligned. */
const SPANS = {
  day: { key: 'day', label: 'Day', days: 1, shiftDays: 7, compareLabel: 'the same day last week' },
  week: { key: 'week', label: 'Week', days: 7, shiftDays: 7, compareLabel: 'the week before' },
  fourWeeks: { key: 'fourWeeks', label: '4 weeks', days: 28, shiftDays: 28, compareLabel: 'the 4 weeks before' },
} as const;
type SpanKey = keyof typeof SPANS;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The mover-row fields the search box matches. */
const MOVER_SEARCH_FIELDS: ReadonlyArray<keyof MoverRow> = ['path', 'title'];

function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Yesterday UTC — the newest day the mirror can hold. */
function defaultAnchor(): string {
  return new Date(Date.now() - DAY_MS).toISOString().slice(0, 10);
}

function shiftYmd(ymd: string, days: number): string {
  return new Date(new Date(`${ymd}T00:00:00.000Z`).getTime() + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * @param props.includeHidden - Count hidden pages too (the Pages screen's "Include hidden pages" switch).
 */
export function PageMoversPanel({ includeHidden = false }: { readonly includeHidden?: boolean } = {}): React.ReactElement {
  const [metric, setMetric] = useState<MoverMetric>('clicks');
  const [span, setSpan] = useState<SpanKey>('day');
  const [anchor, setAnchor] = useState(defaultAnchor);
  // Local, not `?q=`: the panel sits on the Pages screen, which has its own list search.
  // One search narrows both tables, so a page can be followed across drops and gains.
  const [search, setSearch] = useState('');

  const config = SPANS[span];
  // The window ENDS on the anchor day (inclusive): [anchor - days + 1, anchor + 1).
  const from = shiftYmd(anchor, -(config.days - 1));
  const to = shiftYmd(anchor, 1);

  const movers = useQuery({
    queryKey: ['page-movers', from, to, config.shiftDays, metric, includeHidden],
    queryFn: ({ signal }) =>
      readPageMovers({ from, to, shiftDays: config.shiftDays, metric, ...(includeHidden ? { includeHidden } : {}) }, signal),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Panel className="mt-4 p-5">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-portal-card-title font-bold text-portal-ink">Top movers</h2>
          <p className="text-portal-micro text-portal-muted">
            Which pages gained or lost the most, vs {config.compareLabel} — weekday-aligned. Search data for
            the last ~2 days is still provisional.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <SegmentedControl<MoverMetric>
            label="Metric"
            value={metric}
            onChange={setMetric}
            options={METRIC_OPTIONS.map((option) => ({ key: option.key, label: option.label }))}
          />
          <SegmentedControl<SpanKey>
            label="Window"
            value={span}
            onChange={setSpan}
            options={(Object.keys(SPANS) as SpanKey[]).map((key) => ({ key, label: SPANS[key].label }))}
          />
          <label className="block">
            <span className="sr-only">Ending on</span>
            <DatePicker
              aria-label="Ending on"
              value={anchor}
              max={utcToday()}
              onChange={(next) => {
                if (next !== '') setAnchor(next);
              }}
              variant="button"
              className="w-auto"
            />
          </label>
        </div>
      </div>
      {movers.isPending ? (
        <LoadingState label="Comparing windows…" />
      ) : movers.isError ? (
        <ErrorNotice title="Movers unavailable" message={errorMessage(movers.error)} />
      ) : (
        <>
          <SearchInput
            label="Search top movers"
            placeholder="Path or title"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="mb-3 w-full max-w-sm"
          />
          <p className="mb-3 text-portal-micro text-portal-muted">
            {periodLabel(movers.data.range.from, 'day')} – {periodLabel(shiftYmd(movers.data.range.to, -1), 'day')} vs{' '}
            {periodLabel(movers.data.range.compareFrom, 'day')} –{' '}
            {periodLabel(shiftYmd(movers.data.range.compareTo, -1), 'day')}
          </p>
          <div className="grid gap-4 lg:grid-cols-2">
            <MoversTable title="Biggest drops" rows={filterRows(movers.data.drops, search, MOVER_SEARCH_FIELDS)} direction="drop" searching={search.trim() !== ''} />
            <MoversTable title="Biggest gains" rows={filterRows(movers.data.gains, search, MOVER_SEARCH_FIELDS)} direction="gain" searching={search.trim() !== ''} />
          </div>
        </>
      )}
    </Panel>
  );
}

function MoversTable({
  title,
  rows,
  direction,
  searching,
}: {
  readonly title: string;
  readonly rows: readonly MoverRow[];
  readonly direction: 'drop' | 'gain';
  /** A search is narrowing the rows, so an empty table means no match. */
  readonly searching: boolean;
}): React.ReactElement {
  const navigate = useNavigate();
  const pageUrl = (path: string) => `/analytics/pages/detail?path=${encodeURIComponent(path)}`;
  return (
    <Panel>
      <div className="px-5 pt-5 pb-4">
        <h3 className="text-portal-card-title font-bold text-portal-ink">{title}</h3>
      </div>
      {rows.length === 0 && searching ? (
        <div className="px-5 pb-5">
          <EmptyState title="No pages match" message="Try a different search." />
        </div>
      ) : rows.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState
            title={direction === 'drop' ? 'Nothing dropped' : 'Nothing gained'}
            message="No pages moved in this comparison."
          />
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto rounded-b-[8px]">
          <DataTable
            headers={['Page', 'Now', 'Before', 'Change']}
            rightAlignedHeaders={[1, 2, 3]}
            minWidth={420}
            flush
            cards={rows.map((row) => ({
              key: row.path,
              title: row.path,
              ...(row.title ? { subtitle: row.title } : {}),
              fields: [
                { label: 'Now', value: row.current.toLocaleString() },
                { label: 'Before', value: row.previous.toLocaleString() },
                { label: 'Change', value: <MoverChange row={row} /> },
              ],
              onOpen: () => navigate(pageUrl(row.path)),
            }))}
          >
            {rows.map((row) => (
              <tr key={row.path} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <Link
                    to={pageUrl(row.path)}
                    className="block font-bold break-all text-brand"
                  >
                    {row.path}
                  </Link>
                  {row.title ? (
                    <span className="block text-portal-micro text-portal-muted">{row.title}</span>
                  ) : null}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums">{row.current.toLocaleString()}</td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.previous.toLocaleString()}
                </td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap tabular-nums">
                  <MoverChange row={row} />
                </td>
              </tr>
            ))}
          </DataTable>
        </div>
      )}
    </Panel>
  );
}

/** How much a page moved: the count, then the percent. */
function MoverChange({ row }: { readonly row: MoverRow }): React.ReactElement {
  return (
    <>
      <span className={row.delta < 0 ? 'font-bold text-rose-700' : 'font-bold text-portal-success'}>
        {row.delta > 0 ? '+' : ''}
        {row.delta.toLocaleString()}
      </span>
      <DeltaPct current={row.current} previous={row.previous} />
    </>
  );
}
